import { useMemo, useEffect, useState, useCallback } from 'react'
import { fetchInsuranceQuotesForExistingBooking, fetchTravelProtectionQuotesForExistingBooking } from 'actions/InsuranceActions'
import { GetQuotePayload } from 'api/insurance'
import { getOrderDateRange } from 'checkout/Components/Confirmation/BookingDetailsV2/utils'
import { getItineraryCountries } from 'checkout/lib/utils/cruises/itinerary'
import { ValidCountry } from 'checkout/lib/utils/insurance/payload'
import { PROVIDER_COVER_GENIUS } from 'constants/insurance'
import useOffers from 'hooks/Offers/useOffers'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { max, min, nonNullable } from 'lib/array/arrayUtils'
import { isCoverGeniusEnabled, isCoverGeniusEndDateValid } from 'lib/insurance/insuranceHelpers'
import { countOccupants, countTravellersAsOccupants } from 'lib/offer/occupancyUtils'
import { isLEHotel, isCruiseOffer } from 'lib/offer/offerTypes'
import { getUpsellDismissalStorageKey } from 'lib/order/upsellUtils'
import { checkCanViewLuxPlusBenefits, isEnabledLuxPlusDiscountedInsurance } from 'luxPlus/selectors/featureToggle'
import {
  get as getLocalStorage,
  set as setLocalStorage,
} from 'lib/storage/isomorphicLocalStorage'
import { getDestCountriesFromOrder, getInsuranceDatesFromOrder } from 'checkout/selectors/request/insurance'
import { isTravelProtectionAvailableInRegion } from 'selectors/featuresSelectors'
import { isAndroidAppUserAgent, isIOSAppUserAgent } from 'lib/web/deviceUtils'
import getObjectKey from 'lib/object/getObjectKey'

interface InsuranceUpsellData {
  offerId?: string;
  occupants?: App.Occupants;
  destinationCountries?: Array<string | null>;
  destinationCountryCode?: string | null;
  provider?: string;
  reservationType?: string;
  startDate: string;
  endDate: string;
  status?: App.ItemStatus;
  type: string;
}

function useInsuranceUpsell(
  order: App.Order,
): {
  insuranceUpsellEnabled: true
  insuranceUpsell: App.InsuranceUpsell
  handleInsuranceUpsellDismiss: () => void
  insuranceDataLoading: boolean
} | {
  insuranceUpsellEnabled: false
  insuranceUpsell: undefined
  handleInsuranceUpsellDismiss: undefined
  insuranceDataLoading: boolean
} {
  const dispatch = useAppDispatch()
  const [dismissed, setDismissed] = useState<boolean>(!!getLocalStorage(getUpsellDismissalStorageKey('insurance', order.id)))

  const handleInsuranceUpsellDismiss = useCallback(() => {
    setLocalStorage(getUpsellDismissalStorageKey('insurance', order.id), true)
    setDismissed(true)
  }, [order.id])

  const fetchingQuotes = useAppSelector(state => state.insurance.fetchingQuotes)
  const quotes = useAppSelector(state => state.insurance.quotes)
  const fetchingLuxPlusSubscription = useAppSelector(state => state.auth.account.luxPlus.member.subscription.fetching)
  const ableToViewLuxPlusBenefits = useAppSelector(checkCanViewLuxPlusBenefits)
  const luxPlusDiscountedInsuranceEnabled = useAppSelector(isEnabledLuxPlusDiscountedInsurance)
  const isTravelProtectionEnabled = useAppSelector(isTravelProtectionAvailableInRegion)

  const dates = getOrderDateRange(order)

  const offerIds = useMemo(() => [
    ...order.items.map((i) => i.offer.id),
    ...order.cruiseItems.map((i) => i.cruiseOfferId),
  ], [order.cruiseItems, order.items])

  const [offers, fetching] = useOffers(offerIds)

  const orderIsLessThanAYearAway = dates.latestDate ? isCoverGeniusEndDateValid(dates.latestDate) : false
  const insuranceDatesFromOrder = useAppSelector(state => getInsuranceDatesFromOrder(state, order))
  const destCountriesFromOrder = useAppSelector(state => getDestCountriesFromOrder(state, order))
  const isInsuranceSupported = !!(insuranceDatesFromOrder && destCountriesFromOrder?.countries.length)
  const coverGeniusIsEnabled = useAppSelector(state => isCoverGeniusEnabled(state.geo.currentRegionCode))
  const isMobileApp = useAppSelector(state => isIOSAppUserAgent(state.config.rawUserAgentString) || isAndroidAppUserAgent(state.config.rawUserAgentString))
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const orderHasInsurance = order.hasInsurance

  const [earliestItem, latestItem] = useMemo(() => {
    const possibleUpsells: Array<InsuranceUpsellData> = [
      ...nonNullable(order.items.map(item => {
        const hotelOffer = offers.find(offer => offer.id === item.offer.id)
        if (isLEHotel(hotelOffer) && item.offer.property && item.reservation) {
          return {
            offerId: item.offer.id,
            occupants: {
              adults: item.reservation.adults,
              children: item.reservation.children,
              infants: item.reservation.infants,
            },
            destinationCountries: [hotelOffer.property.geoData.country],
            destinationCountryCode: hotelOffer.property.geoData.countryCode,
            reservationType: item.reservationType,
            startDate: item.reservation.startDate,
            endDate: item.reservation.endDate,
            status: item.status,
            type: 'hotel',
          }
        }
      })),
      ...nonNullable(order.bedbankItems.map(item => {
        const occupancies: Array<App.Occupants> = item.rooms.map(room => room.occupancy)
        const occupants = countOccupants(occupancies)

        return {
          offerId: item.offer.id,
          occupants: {
            adults: occupants?.adults,
            children: occupants?.children,
            infants: occupants?.infants,
          },
          destinationCountries: [item.offer.property.address.countryName],
          reservationType: item?.reservationId,
          startDate: item.checkIn.format('YYYY-MM-DD'),
          endDate: item.checkOut.format('YYYY-MM-DD'),
          status: item.status,
          type: 'bedbank_hotel',
        }
      })),
      ...nonNullable(order.tourItems.map(item => {
        const travellers = countTravellersAsOccupants(item.tour.travellers)

        return {
          offerId: item.tourId,
          occupants: {
            adults: travellers.adults,
            children: travellers.children,
            infants: travellers.infants,
          },
          destinationCountries: item.tour.countriesVisited,
          reservationType: item.reservationId,
          startDate: item.tour.startDate,
          endDate: item.tour.endDate,
          status: item.status,
          type: 'tour',
        }
      })),
      ...nonNullable(order.cruiseItems.map(item => {
        const cruiseOffer = offers.find(offer => offer.id === item.cruiseOfferId)

        const passengers = countTravellersAsOccupants(item.passengers)
        if (isCruiseOffer(cruiseOffer)) {
          return {
            offerId: item.cruiseOfferId,
            occupants: {
              adults: passengers.adults,
              children: passengers.children,
              infants: passengers.infants,
            },
            destinationCountries: getItineraryCountries(cruiseOffer),
            reservationType: item.reservationId,
            startDate: item.departureDate,
            endDate: item.arrivalDate,
            status: item.status,
            type: 'cruise',
          }
        }
      })),
    ].filter(item => item.status === 'completed')

    const earliest = min(possibleUpsells, item => new Date(item.startDate))
    const latest = max(possibleUpsells, item => new Date(item.endDate))

    return [earliest, latest]
  }, [order, offers])

  const insuranceFetchKey = getObjectKey({
    order: order?.id,
    startDate: earliestItem?.startDate,
    endDate: latestItem?.endDate,
    destinationCountries: earliestItem?.destinationCountries,
    total: order?.total,
    adults: earliestItem?.occupants?.adults,
    children: earliestItem?.occupants?.children,
    infants: earliestItem?.occupants?.infants,
    isCruise: earliestItem?.type === 'cruise',
  })

  useEffect(() => {
    if (latestItem && earliestItem && order && orderIsLessThanAYearAway) {
      // cannot quote if destination country unavailable
      if (!earliestItem.destinationCountries) return
      const commonParams: Partial<GetQuotePayload> = {
        travellers_details: {
          number_of_adults: earliestItem?.occupants?.adults ?? 0,
          number_of_children: earliestItem?.occupants?.children ?? 0,
          number_of_infants: earliestItem?.occupants?.infants ?? 0,
          number_of_seniors: 0,
        },
        destination_countries: earliestItem?.destinationCountries as Array<ValidCountry>,
        provider: PROVIDER_COVER_GENIUS,
        trip_start_date: earliestItem?.startDate,
        trip_end_date: latestItem?.endDate,
        total_price: order.total,
        isCruise: earliestItem?.type === 'cruise',
      }
      if (isTravelProtectionEnabled) {
        dispatch(fetchTravelProtectionQuotesForExistingBooking({
          ...commonParams,
          session_id: order.id,
          metadata: {
            sales_channel: isSpoofed ? 'agent' : 'customer',
          },
          mobile_only_price: isMobileApp,
        }))
      } else {
        dispatch(fetchInsuranceQuotesForExistingBooking(order.id, {
          ...commonParams,
          reservationType: earliestItem?.reservationType,
        }))
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, insuranceFetchKey, isMobileApp, isSpoofed, isTravelProtectionEnabled, orderIsLessThanAYearAway])

  const [cheapestQuotePrice, cheapestQuoteLuxPlusPrice] = useMemo(() => {
    let cheapestQuotePrice = 0
    let cheapestQuoteLuxPlusPrice = 0

    if (quotes && earliestItem && latestItem) {
      const policyQuotes = quotes.filter(item => (
        item.startDate === earliestItem?.startDate &&
        item.endDate === latestItem?.endDate
      )).map(item => item.policyQuotes).flat()

      const quotePrices: Array<number> = policyQuotes.map(item => item.pricePerPerson)
      cheapestQuotePrice = min(quotePrices) ?? 0

      if (ableToViewLuxPlusBenefits && luxPlusDiscountedInsuranceEnabled) {
        const quoteLuxPlusPrices: Array<number> = policyQuotes.map(item => item.luxPlusPricePerPerson)
        cheapestQuoteLuxPlusPrice = min(quoteLuxPlusPrices) ?? 0
      }
    }
    return [cheapestQuotePrice, cheapestQuoteLuxPlusPrice]
  }, [ableToViewLuxPlusBenefits, earliestItem, latestItem, luxPlusDiscountedInsuranceEnabled, quotes])

  const enabled =
  !dismissed &&
  isInsuranceSupported &&
  coverGeniusIsEnabled &&
  !orderHasInsurance &&
  orderIsLessThanAYearAway

  const insuranceUpsell = useMemo<App.InsuranceUpsell>(() => {
    return {
      checkCanViewLuxPlusBenefits: ableToViewLuxPlusBenefits,
      isEnabledLuxPlusDiscountedInsurance: luxPlusDiscountedInsuranceEnabled,
      fetchingLuxPlusSubscription,
      fetchingQuotes,
      cheapestQuotePrice,
      cheapestQuoteLuxPlusPrice,
      orderId: order.id,
    }
  }, [
    ableToViewLuxPlusBenefits,
    cheapestQuoteLuxPlusPrice,
    cheapestQuotePrice,
    fetchingLuxPlusSubscription,
    fetchingQuotes,
    luxPlusDiscountedInsuranceEnabled,
    order.id,
  ])

  if (enabled) {
    return {
      insuranceUpsellEnabled: true,
      insuranceUpsell,
      handleInsuranceUpsellDismiss,
      insuranceDataLoading: fetching,
    }
  } else {
    return {
      insuranceUpsellEnabled: false,
      insuranceUpsell: undefined,
      handleInsuranceUpsellDismiss: undefined,
      insuranceDataLoading: fetching,
    }
  }
}

export default useInsuranceUpsell
